import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_ANNOUNCEMENTS": {
      const announcements = action.payload;
      const newAnnouncements = [];
      if (isArray(announcements)) {
        announcements.forEach((announcement) => {
          const idx = state.findIndex((u) => u.id === announcement.id);
          if (idx !== -1) {
            state[idx] = announcement;
          } else {
            newAnnouncements.push(announcement);
          }
        });
      }
      return [...state, ...newAnnouncements];
    }
    case "UPDATE_ANNOUNCEMENTS": {
      const announcement = action.payload;
      const idx = state.findIndex((u) => u.id === announcement.id);
      if (idx !== -1) {
        state[idx] = announcement;
        return [...state];
      }
      return [announcement, ...state];
    }
    case "DELETE_ANNOUNCEMENT": {
      const announcementId = action.payload;
      const idx = state.findIndex((u) => u.id === announcementId);
      if (idx !== -1) state.splice(idx, 1);
      return [...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------
const Announcements = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);

  // ---------------------------------------------------------------------------
  // Permissão (super user only)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => { fetchAnnouncements(); }, 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber }
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  // ---------------------------------------------------------------------------
  // Socket
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (user.companyId) {
      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
      };
      socket.on(`company-announcement`, onCompanyAnnouncement);
      return () => socket.off(`company-announcement`, onCompanyAnnouncement);
    }
  }, [user, socket]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
        await api.delete(`/announcements/${announcement.id}/media-upload`);
      await api.delete(`/announcements/${announcement.id}`);
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const translatePriority = (val) => {
    switch (val) {
      case 1: return "Alta";
      case 2: return "Média";
      case 3: return "Baixa";
      default: return "-";
    }
  };

  const getPriorityChip = (priority) => {
    const configs = {
      1: { bg: "rgba(239, 68, 68, 0.08)",  color: "#ef4444", label: "Alta", border: "rgba(239, 68, 68, 0.2)" },
      2: { bg: "rgba(245, 158, 11, 0.08)", color: "#f59e0b", label: "Média", border: "rgba(245, 158, 11, 0.2)" },
      3: { bg: "rgba(16, 185, 129, 0.08)", color: "#10b981", label: "Baixa", border: "rgba(16, 185, 129, 0.2)" }
    };
    const cfg = configs[priority] || configs[3];
    return (
      <Chip
        label={cfg.label}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          bgcolor: cfg.bg,
          color: cfg.color,
          border: "1px solid",
          borderColor: cfg.border
        }}
      />
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <CampaignIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("announcements.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerenciamento de anúncios
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* Search */}
            <TextField
              placeholder={i18n.t("announcements.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  "& fieldset": { border: "none" },
                  "&:hover": { 
                    borderColor: PRIMARY_MAIN,
                    bgcolor: "action.hover"
                  },
                  "&.Mui-focused": { 
                    borderColor: PRIMARY_MAIN,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                  }
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.25,
                  fontSize: "0.875rem",
                  fontWeight: 500
                }
              }}
            />

            {/* Add button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAnnouncementModal}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              {i18n.t("announcements.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Tabela */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho da tabela */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {searchParam ? `Resultados da busca` : "Todos os anúncios"}
              </Typography>
              <Chip
                label={`${announcements.length} ${announcements.length !== 1 ? "anúncios" : "anúncio"}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  bgcolor: "rgba(148, 163, 184, 0.08)",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "rgba(148, 163, 184, 0.2)"
                }}
              />
            </Stack>
          </Box>

          {/* Grid */}
          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    i18n.t("announcements.table.title"),
                    i18n.t("announcements.table.priority"),
                    i18n.t("announcements.table.mediaName"),
                    i18n.t("announcements.table.status"),
                    i18n.t("announcements.table.actions")
                  ].map((label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        letterSpacing: "0.5px",
                        bgcolor: "background.paper"
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {announcements.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum anúncio encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  announcements.map((announcement) => (
                    <TableRow
                      key={announcement.id}
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                          transition: "background-color 0.2s"
                        }
                      }}
                    >
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                          {announcement.title}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getPriorityChip(announcement.priority)}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                          {announcement.mediaName ? (
                            <>
                              <AttachFileIcon sx={{ color: "#10b981", fontSize: 18 }} />
                              <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 500, fontSize: "0.875rem" }}>
                                {announcement.mediaName}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ color: "text.disabled", fontSize: "0.875rem" }}>
                              {i18n.t("quickMessages.noAttachment")}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={announcement.status ? i18n.t("announcements.active") : i18n.t("announcements.inactive")}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: announcement.status ? "rgba(16, 185, 129, 0.08)" : "rgba(148, 163, 184, 0.08)",
                            color: announcement.status ? "#10b981" : "text.secondary",
                            border: "1px solid",
                            borderColor: announcement.status ? "rgba(16, 185, 129, 0.2)" : "rgba(148, 163, 184, 0.2)"
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEditAnnouncement(announcement)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: PRIMARY_MAIN, bgcolor: `${PRIMARY_MAIN}10` }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setConfirmModalOpen(true);
                                setDeletingAnnouncement(announcement);
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "error.main", bgcolor: "rgba(239, 68, 68, 0.08)" }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && announcements.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={
          deletingAnnouncement
            ? `${i18n.t("announcements.confirmationModal.deleteTitle")} ${deletingAnnouncement.title}?`
            : ""
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />
    </Box>
  );
};

export default Announcements;